import { createRouter, createWebHistory } from 'vue-router'
import { useTitle } from '@vueuse/core'
import HomeView from '../views/home/HomeView.vue'
import {
  verifyPhoneBind,
  verifyUserBeforeTrade,
  verifyPaymentPassword,
  toastify,
  verifyKYC,
  getDevice,
} from '@/utils'
import { csManager } from '@/utils/customerService'
import { useSessionStore } from '@/stores/session'
import { useAssetsStore } from '@/stores/assets'
import { useAuthStore } from '@/stores/auth'
import { UserRole } from '@/enums'
import { memberExchangeApi } from '@/api/member'
import { setSuppressAuthRedirect } from '@/api/request'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    // 否则滚动到顶部
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { title: '首页' /** chunkName: home */ },
    },
    {
      path: '/quick-buy-coin',
      name: 'quick-buy-coin',
      component: () => import('../views/quick-buy-coin/QuickBuyCoin.vue'),
      meta: { title: '快速买币', loginRequired: true },
    },
    {
      path: '/quick-buy-coin/failure',
      name: 'quick-buy-coin-failure',
      component: () => import('../views/quick-buy-coin/QuickBuyCoinFailure.vue'),
      meta: { title: '快速买币失败', loginRequired: true },
    },
    {
      path: '/quick-sell-coin',
      name: 'quick-sell-coin',
      component: () => import('../views/quick-sell-coin/QuickSellCoin.vue'),
      meta: { title: '快速卖币', loginRequired: true },
    },
    {
      path: '/quick-sell-coin/order-list',
      name: 'quick-sell-coin-order-list',
      component: () => import('../views/quick-sell-coin/QuickSellCoinOrderList.vue'),
      meta: { title: '卖币订单', loginRequired: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/login/LoginView.vue'),
      meta: { title: '登录' /** chunkName: login */ },
    },
    {
      path: '/login_v2',
      name: 'login_v2',
      component: () => import('../views/login/LoginV2View.vue'),
      meta: { title: '登录' /** chunkName: login */ },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/register/RegisterView.vue'),
      meta: { title: '注册' /** chunkName: login */ },
    },
    {
      path: '/qr-scanner',
      name: 'QRScanner',
      component: () => import('../views/qr-scanner/QRScanner.vue'),
      meta: { title: '扫一扫' },
    },
    {
      path: '/reset-pwd/step1',
      name: 'ResetPasswordStep1',
      component: () => import('@/views/reset-pwd/ResetPasswordStep1.vue'),
      meta: { title: '重置密码' },
    },
    {
      path: '/reset-pwd/step2',
      name: 'ResetPasswordStep2',
      component: () => import('@/views/reset-pwd/ResetPasswordStep2.vue'),
      meta: { title: '安全验证', requiresResetPhone: true },
    },
    {
      path: '/reset-pwd/step3',
      name: 'ResetPasswordStep3',
      component: () => import('@/views/reset-pwd/ResetPasswordStep3.vue'),
      meta: { title: '设置新密码', requiresResetToken: true },
    },
    {
      path: '/trade',
      name: 'trade',
      component: () => import('../views/c2c/TradeView.vue'),
      meta: { title: '买卖币', loginRequired: true /** chunkName: home */ },
    },
    {
      path: '/usdt-buy-coin',
      name: 'usdt-buy-coin',
      component: () => import('../views/c2c/UsdtBuyCoinView.vue'),
      meta: { title: 'USDT买LB', loginRequired: true },
    },
    {
      path: '/usdt-buy-coin/success',
      name: 'usdt-buy-coin-success',
      component: () => import('../views/c2c/USDTBuyCoinViewSuc.vue'),
      meta: { title: '购买成功', loginRequired: true },
    },
    {
      path: '/usdt-buy-coin/records',
      name: 'usdt-buy-coin-records',
      component: () => import('../views/c2c/USDTBuyCoinRecordsView.vue'),
      meta: { title: '兑换记录', loginRequired: true },
    },
    {
      path: '/buy-coin/:id',
      name: 'buy-coin-detail',
      component: () => import('../views/c2c/BuyOrderDetailView.vue'),
      meta: { title: '购买详情', loginRequired: true },
      async beforeEnter(_to, _from, next) {
        try {
          await verifyUserBeforeTrade()
          next()
        } catch {
          next(false)
        }
      },
    },
    {
      path: '/sell-coin',
      name: 'sell-coin',
      component: () => import('../views/c2c/SellCoinView.vue'),
      meta: { title: '卖币', loginRequired: true },
    },
    {
      path: '/sell-coin/:id',
      name: 'sell-coin-detail',
      component: () => import('../views/c2c/SellCoinDetailView.vue'),
      meta: { title: '出售详情', loginRequired: true },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/orders/OrdersView.vue'),
      meta: { title: '订单', loginRequired: true /** chunkName: home */ },
    },
    {
      path: '/order/:order_id',
      name: 'order-detail',
      component: () => import('../views/orders/OrderDetailView.vue'),
      meta: { title: '订单详情', loginRequired: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/profile/ProfileView.vue'),
      meta: { title: '个人中心', loginRequired: true /** chunkName: home */ },
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/chat/ChatView.vue'),
      meta: { title: '聊天', loginRequired: true },
    },
    {
      path: '/transfer',
      name: 'transfer',
      component: () => import('../views/transfer/TransferView.vue'),
      meta: { title: '转账', loginRequired: true },
      async beforeEnter(_to, _from, next) {
        const assetStore = useAssetsStore()
        if (assetStore.isAgent) {
          toastify.warning('抱歉，商家不允许此交易')
          next(false)
        } else {
          next()
        }
      },
    },
    {
      path: '/user',
      name: 'user',
      meta: { loginRequired: true },
      children: [
        {
          path: 'verify-real',
          name: 'VerifyReal',
          component: () => import('../views/user/verify-real/VerifyReal.vue'),
          meta: { title: '身份认证' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'phone-bind',
          name: 'BindPhoneStep1',
          component: () => import('../views/user/bind-phone/BindPhoneStep1.vue'),
          meta: { title: '绑定手机/邮箱' },
        },
        {
          path: 'phone-bind/step2',
          name: 'BindPhoneStep2',
          component: () => import('../views/user/bind-phone/BindPhoneStep2.vue'),
          meta: { title: '安全验证' },
        },
        {
          path: 'set-pin',
          name: 'SetPin',
          component: () => import('../views/user/set-pin/SetPin.vue'),
          meta: { title: '设置支付密码' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              // await verifyKYC()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'reset-pin/step1',
          redirect: { name: 'ResetPinStep2' },
        },
        {
          path: 'reset-pin/step2',
          name: 'ResetPinStep2',
          component: () => import('../views/user/reset-pin/ResetPinStep2.vue'),
          meta: { title: '安全验证' },
        },
        {
          path: 'reset-pin/step3',
          name: 'ResetPinStep3',
          component: () => import('../views/user/reset-pin/ResetPinStep3.vue'),
          meta: { title: '找回支付密码', requiresResetToken: true },
        },
        {
          path: 'pay-methods',
          name: 'PayMethods',
          component: () => import('../views/user/pay-methods/PayMethods.vue'),
          meta: { title: '收付款管理' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              await verifyPaymentPassword()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'pay-methods/add',
          name: 'AddPayMethod',
          component: () => import('../views/user/pay-methods/AddPayMethod.vue'),
          meta: { title: '添加收付款方式' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              await verifyPaymentPassword()
              await verifyKYC()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'pay-methods/add-new',
          name: 'AddPayMethodNew',
          component: () => import('../views/user/pay-methods/AddPayMethodNew.vue'),
          meta: { title: '新增收付款方式' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              await verifyPaymentPassword()
              await verifyKYC()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'edit-nickname',
          name: 'EditNickname',
          component: () => import('../views/user/edit-nickname/EditNickname.vue'),
          meta: { title: '修改昵称' },
        },
        {
          path: 'profile-info',
          name: 'UserProfile',
          component: () => import('../views/user/profile/UserProfileView.vue'),
          meta: { title: '个人信息' },
        },
        {
          path: 'payment-qrcode',
          name: 'PaymentQRCode',
          component: () => import('../views/user/payment-qrcode/PaymentQRCode.vue'),
          meta: { title: '收款码' },
        },
        {
          path: 'security',
          name: 'SecurityCenter',
          component: () => import('../views/user/security/SecurityCenterView.vue'),
          meta: { title: '安全中心' },
        },
        {
          path: 'security/change-login-password',
          name: 'ChangeLoginPassword',
          component: () => import('../views/user/security/ChangeLoginPasswordView.vue'),
          meta: { title: '修改登录密码', loginRequired: true },
        },
        {
          path: 'devices',
          name: 'Devices',
          component: () => import('../views/user/security/DevicesView.vue'),
          meta: { title: '设备' },
        },
        {
          path: 'bills',
          name: 'Bills',
          component: () => import('../views/user/bills/BillsView.vue'),
          meta: { title: '我的账单' },
        },
        {
          path: 'reports',
          name: 'Reports',
          component: () => import('../views/user/reports/ReportsView.vue'),
          meta: { title: '维权举报' },
        },
        {
          path: 'appeals',
          name: 'Appeals',
          component: () => import('../views/profile/AppealListView.vue'),
          meta: { title: '工单列表' },
        },
        {
          path: 'business',
          name: 'Business',
          component: () => import('../views/user/business/Business.vue'),
          meta: { title: '商家认证' },
        },
        {
          path: 'merchant-benefits',
          name: 'MerchantBenefits',
          component: () => import('../views/user/business/MerchantBenefits.vue'),
          meta: { title: '商家权益' },
        },
        {
          path: 'commission',
          name: 'Commission',
          component: () => import('../views/user/business/CommissionView.vue'),
          meta: { title: '码商佣金', loginRequired: true },
        },
        {
          path: 'notify',
          name: 'NotifyCenter',
          component: () => import('../views/user/notify/NotifyCenterView.vue'),
          meta: { title: '通知中心' },
        },
        {
          path: 'feedback',
          name: 'Feedback',
          component: () => import('../views/user/feedback/FeedbackView.vue'),
          meta: { title: '意见反馈', loginRequired: true },
        },
        {
          path: 'feedback/list',
          name: 'FeedbackList',
          component: () => import('../views/user/feedback/FeedbackListView.vue'),
          meta: { title: '我的反馈', loginRequired: true },
        },
        {
          path: 'settings',
          name: 'Settings',
          component: () => import('../views/user/settings/SettingsView.vue'),
          meta: { title: '设置' },
        },
        {
          path: 'share',
          name: 'Share',
          component: () => import('../views/user/share/ShareView.vue'),
          meta: { title: '分享' },
        },
        {
          path: 'game-bind',
          name: 'GameBindList',
          component: () => import('../views/user/game-bind/GameBindListView.vue'),
          meta: { title: '游戏商绑定', loginRequired: true },
        },
      ],
    },
    {
      path: '/merchant/bind',
      name: 'MerchantBind',
      component: () => import('../views/user/game-bind/GameBindConfirmView.vue'),
      meta: { title: '绑定快捷支付', loginRequired: true },
    },
    {
      path: '/500',
      name: '500',
      component: () => import('../views/error/500.vue'),
      meta: { title: '500' },
    },
    {
      path: '/activity/lucky-wheel',
      name: 'LuckyWheel',
      component: () => import('../views/activity/LuckyWheelView.vue'),
      meta: { title: '幸运大转盘', loginRequired: true },
    },
    {
      path: '/activity/lucky-wheel/draw-records',
      name: 'LuckyWheelDrawRecords',
      component: () => import('../views/activity/LuckyWheelDrawRecordsView.vue'),
      meta: { title: '中奖记录', loginRequired: true },
    },
    {
      path: '/activity/lucky-wheel/bonus-records',
      name: 'LuckyWheelBonusRecords',
      component: () => import('../views/activity/LuckyWheelBonusRecordsView.vue'),
      meta: { title: '彩金明细', loginRequired: true },
    },
    {
      path: '/activity/lucky-wheel/coupon-records',
      name: 'LuckyWheelCouponRecords',
      component: () => import('../views/activity/LuckyWheelCouponRecordsView.vue'),
      meta: { title: '彩金券明细', loginRequired: true },
    },
    {
      path: '/activity/lucky-wheel/rules',
      name: 'LuckyWheelRules',
      component: () => import('../views/activity/LuckyWheelRulesView.vue'),
      meta: { title: '活动规则', loginRequired: false },
    },
  ],
})

// 路由切换时，若客服当前已打开则关闭并取消导航
router.beforeEach(async (to, _from, next) => {
  // 检测 mt 参数，在任何组件挂载和请求之前完成商户会员免密登录
  const mt = to.query.mt as string | undefined
  if (mt) {
    // 抑制 401 跳转，避免旧 session 过期时被踢出去
    setSuppressAuthRedirect(true)
    try {
      await memberExchangeApi({ token: mt, device_name: getDevice() })
      console.log('mt', mt)
      const authStore = useAuthStore()
      await authStore.afterLoginSuccess()
    } catch (error) {
      console.error('[Router] 商户会员登录失败:', error)
      toastify({ message: (error as Error).message || '商户会员登录失败', type: 'danger' })
    } finally {
      setSuppressAuthRedirect(false)
    }
    // 清除 mt 参数后继续导航，避免刷新重复登录
    next({ ...to, query: { ...to.query, mt: undefined }, replace: true })
    return
  }

  console.log('Route change detected, checking CS status...', csManager.isOpen)
  // 有 bug ，先注释
  if (csManager.isOpen) {
    csManager.close()
    next(false)
    return
  }

  // 未登录时访问受保护页面，跳转到登录页
  const privateRoutes = ['profile', 'orders', 'trade']
  if (privateRoutes.includes(to.name as string) || to.meta.loginRequired) {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) {
      next({ name: 'login' })
      return
    }
  }

  next()
})

// 路由守卫：设置页面标题
router.afterEach((to) => {
  const defaultTitle = import.meta.env.VITE_APP_TITLE || 'KW客户端'
  const pageTitle = to.meta.title as string | undefined

  if (pageTitle) {
    useTitle(`${pageTitle} - ${defaultTitle}`)
  } else {
    useTitle(defaultTitle)
  }
})

export default router
